/**
 * Get environment variable with runtime injection support.
 *
 * In production, docker-entrypoint.sh injects environment variables into window.__env
 * at container startup. This allows the same Docker image to be deployed to different
 * environments (dev, staging, prod) with environment-specific configuration.
 *
 * For local development, falls back to import.meta.env (Vite build-time values).
 *
 * @param key - The environment variable key (e.g., 'VITE_APP_URL')
 * @param defaultValue - Optional default value if the env var is not found
 * @returns The environment variable value, or empty string if not found
 *
 * @example
 * // Instead of:
 * const url = import.meta.env.VITE_APP_URL;
 *
 * // Use:
 * const url = getEnvVar('VITE_APP_URL');
 */
export const getEnvVar = (
  key: keyof ImportMetaEnv,
  { defaultValue = '' }: { defaultValue?: string } = {},
): string => {
  // 1. Try runtime value first (injected by docker-entrypoint.sh in production)
  //    This allows the same Docker image to work across dev/staging/prod
  const runtimeValue = window.__env?.[key];
  if (typeof runtimeValue === 'string') {
    return runtimeValue;
  }

  // 2. Fall back to build-time value (Vite's import.meta.env)
  //    Used in local development where there's no Docker container
  const buildTimeValue = import.meta.env[key] as string | undefined;
  if (typeof buildTimeValue === 'string') {
    return buildTimeValue;
  }

  // 3. Log error if env var is missing, return empty string
  //    Some vars are intentionally missing/empty for relative paths
  // console.error(`Missing environment variable: ${String(key)}`);
  return defaultValue;
};
